'use client';

import { cn } from '@coas/ui/lib';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { memo, useMemo, useRef } from 'react';
import { mergeProps, useLongPress, usePress } from 'react-aria';
import type { RouterOutputs } from 'trpc/shared';
import { PriceWithDiscount } from '~/entities/DisplayedPrice';
import { ToggleWishlistButton } from '~/entities/Wishlist';
import { useProductSelected } from '~/features/ProductSelection';
import IconCheckedCircle from '~/shared/assets/icons/checked-circle.svg';
import { makeProductImgLoader } from '~/shared/lib';
import { useSessionAdmin } from '~/shared/lib/auth';
// import IconFolder from '~/shared/assets/icons/folder.svg';
// import IconPerson from '~/shared/assets/icons/person.svg';

type ProductCardProps = {
    product: RouterOutputs['product']['getProductsInfinite']['products'][number];
};

const IGNORE_MS_AFTER_LONGPRESS = 250;

export default memo(({ product }: ProductCardProps) => {
    const { isAdmin } = useSessionAdmin();
    const router = useRouter();

    const { selectionMode, selected, setSelected } = useProductSelected(product);

    const ignorePressUntilRef = useRef(0);

    const { longPressProps } = useLongPress({
        threshold: 420,
        onLongPress: () => {
            if (!isAdmin) return;
            setSelected(!selected);
            ignorePressUntilRef.current = Date.now() + IGNORE_MS_AFTER_LONGPRESS;
        },
    });

    const { pressProps } = usePress({
        onPress: () => {
            if (!isAdmin) return;

            if (Date.now() < ignorePressUntilRef.current) {
                ignorePressUntilRef.current = 0;
                return;
            }

            if (selectionMode) {
                setSelected(!selected);
            } else if (selected) {
                setSelected(false);
            } else {
                router.push(`/products/${product.slug}`);
            }
        },
    });

    const images = useMemo(() => {
        return product.productImages.map(({ image }, _, { length }) => {
            const loader = makeProductImgLoader(image);

            const origW = image.width ?? 0;
            const origH = image.height ?? 0;

            return (
                <Image
                    key={image.id}
                    loader={loader}
                    src={image.key}
                    placeholder={'blur'}
                    blurDataURL={image.lqip ?? undefined}
                    className={cn(
                        length === 2 && 'first:z-20 last:z-10',
                        length === 2 &&
                            'first:opacity-100 group-hover:first:opacity-0 pointer-coarse:group-hover:first:opacity-0',
                        length === 2 &&
                            'last:opacity-0 group-hover:last:opacity-100 pointer-coarse:group-hover:last:opacity-100',
                        origW > origH ? 'object-contain' : 'object-cover',
                    )}
                    alt={image.alt ?? ''}
                    fill
                />
            );
        });
    }, [product.productImages]);

    const { isAvailable, isConst } = useMemo(() => {
        return {
            isAvailable: product.displayPrice.price !== null,
            isConst: product.displayPrice.strategy === 'const',
        };
    }, [product.displayPrice]);

    const handleLinkClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
        if (!isAdmin) return;

        if (selectionMode || Date.now() < ignorePressUntilRef.current) {
            e.preventDefault();
            ignorePressUntilRef.current = 0;
        }
    };

    const t = useTranslations();

    return (
        <div className={'flex flex-col gap-y-3'} {...(isAdmin ? mergeProps(pressProps, longPressProps) : {})}>
            <div className={cn('relative aspect-product w-full bg-white', selected && 'border-2 border-sanity-accent')}>
                {/* {authorized && product.bookmark && (
                    <div className={'absolute top-0 left-page z-40 overflow-hidden'}>
                        <IconBookmark className={'-translate-y-1 size-8 fill-sanity-accent stroke-sanity-accent'} />
                    </div>
                )}
                {authorized && !!product.lists?.length && (
                    <div className={'absolute bottom-page-1/2 left-page z-40 overflow-hidden'}>
                        <IconFolder className={'size-12 fill-sanity-accent stroke-sanity-accent'} />
                        <span
                            className={
                                'absolute inset-0 m-auto size-fit translate-y-0.5 font-700 text-20 text-neutral-50'
                            }
                        >
                            {product.lists.length}
                        </span>
                    </div>
                )}
                {authorized && product.hasPersonOnFirstImage && (
                    <div className={'absolute top-1 right-page z-40 overflow-hidden'}>
                        <IconPerson className={'size-10 fill-sanity-accent'} />
                    </div>
                )} */}
                {selected && (
                    <div
                        className={
                            '-outline-offset-2 absolute top-1 right-1 z-40 h-6 w-6 rounded-full bg-neutral-50 outline outline-2 outline-sanity-accent'
                        }
                    >
                        <IconCheckedCircle className={'size-full fill-sanity-accent'} />
                    </div>
                )}
                {!!images?.length && (
                    <Link
                        href={`/products/${product.slug}`}
                        onClick={handleLinkClick}
                        className={cn(
                            'group relative block h-full w-full overflow-hidden',
                            selectionMode && 'pointer-events-none',
                        )}
                        aria-disabled={selectionMode || undefined}
                        tabIndex={selectionMode ? -1 : 0}
                    >
                        <div className='relative w-full bg-white aspect-[3/4]'>{images}</div>
                    </Link>
                )}
                <ToggleWishlistButton
                    productId={product.id}
                    className='absolute bottom-2 right-2 z-30 h-9 w-9 bg-white/80 p-2'
                    selectedClassName='bg-white'
                />
            </div>
            <Link
                href={`/products/${product.slug}`}
                onClick={handleLinkClick}
                className={cn('flex flex-col gap-y-0.5', selectionMode && 'pointer-events-none')}
                aria-disabled={selectionMode || undefined}
                tabIndex={selectionMode ? -1 : 0}
            >
                <span className={'line-clamp-2 font-futura leading-tight'}>{product.name}</span>

                <span className={'font-600 font-futura'}>
                    {isAvailable && isConst && <PriceWithDiscount displayPrice={product.displayPrice} />}

                    {isAvailable && !isConst && (
                        <>
                            {t('productCard.fromPrice')} <PriceWithDiscount displayPrice={product.displayPrice} />
                        </>
                    )}

                    {!isAvailable && t('productCard.outOfStock')}
                </span>
            </Link>
        </div>
    );
});
